/** The session clock, as two selects that cannot leave the session.
 *
 * Replaces a native `<input type="time">`, which took a `min` but no `max`
 * and offered every minute of the day regardless. `clampTo` caught what came
 * out of it, so the CLOCK was never wrong — but the reader could still pick
 * 16:45 and watch it silently snap to 15:29, which reads as the control
 * ignoring them.
 *
 * Operator, 2026-09-04: *"simulator should always start for 9:16 and end
 * 15:29 … when 15 hour disable from 30 … when 9 hour disable min from 0 to
 * 15"*. So the minutes for an hour are DISABLED rather than hidden: seeing
 * 09:05 greyed says "the session has not started", where an absent option
 * would just look like a short list.
 *
 * The floor is honoured too. Once a basket exists you cannot step back past
 * its first entry (see `simulatorClock.step`), and the same wall applies
 * here — otherwise the picker offers a minute the step buttons refuse.
 */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import { SESSION_CLOSE, SESSION_OPEN, fromMinutes, toMinutes } from "@/lib/simulatorClock";

const MINUTES_IN_HOUR = 60;

interface Props {
  /** "HH:MM" — the clock's current minute. */
  at: string;
  /** Lower bound, at or after SESSION_OPEN. */
  floor: string;
  onAt: (next: string) => void;
}

/** Inclusive [low, high] the session allows, in minutes past midnight. */
function bounds(floor: string): [number, number] {
  return [Math.max(toMinutes(SESSION_OPEN), toMinutes(floor)), toMinutes(SESSION_CLOSE)];
}

/** Hours with at least one selectable minute — 9 through 15 in practice,
 * fewer once a floor is set late in the day. */
function hours(low: number, high: number): number[] {
  const first = Math.floor(low / MINUTES_IN_HOUR);
  const last = Math.floor(high / MINUTES_IN_HOUR);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

export function TimePicker({ at, floor, onAt }: Readonly<Props>): ReactElement {
  const [low, high] = bounds(floor);
  const current = toMinutes(at);
  const hour = Math.floor(current / MINUTES_IN_HOUR);
  const minute = current % MINUTES_IN_HOUR;

  /** Moving the HOUR keeps the minute where possible and otherwise lands on
   * the nearest legal one — jumping to 15 from 09:45 gives 15:29, not a
   * refusal or a silent snap to the top of the hour. */
  const pickHour = (h: number): void => {
    const wanted = h * MINUTES_IN_HOUR + minute;
    onAt(fromMinutes(Math.min(Math.max(wanted, low), high)));
  };

  const pickMinute = (m: number): void => {
    onAt(fromMinutes(hour * MINUTES_IN_HOUR + m));
  };

  // ⚠️ WIDTH IS LOAD-BEARING. `input` pads horizontally and the native
  // arrow takes ~20px; at w-12 (48px) Windows Chrome clipped "09" to its
  // left arc and rendered "(" with the minute blank - the clock was right,
  // the control could not show it (operator screenshot, 2026-09-06).
  const select = "input h-7 py-0 px-1.5 pr-5 font-mono text-xs tabular-nums text-center";
  return (
    <div className="flex items-center gap-0.5" role="group" aria-label="Time (IST)">
      <select
        aria-label="Hour (IST)"
        className={cn(select, "w-[3.75rem]")}
        value={hour}
        onChange={(e) => {
          pickHour(Number(e.target.value));
        }}
      >
        {hours(low, high).map((h) => (
          <option key={h} value={h}>
            {String(h).padStart(2, "0")}
          </option>
        ))}
      </select>
      <span className="text-xs text-ink-muted">:</span>
      <select
        aria-label="Minute (IST)"
        className={cn(select, "w-[3.75rem]")}
        value={minute}
        onChange={(e) => {
          pickMinute(Number(e.target.value));
        }}
      >
        {Array.from({ length: MINUTES_IN_HOUR }, (_, m) => {
          const abs = hour * MINUTES_IN_HOUR + m;
          return (
            <option key={m} value={m} disabled={abs < low || abs > high}>
              {String(m).padStart(2, "0")}
            </option>
          );
        })}
      </select>
    </div>
  );
}
